import { useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { Switch } from "@/components/design/Switch";
import { LockClosedIcon } from "@/components/match/lock-icons";
import { groupLabel } from "@/pages/settings/access-group-labels";
import type { PersonalShareSelection } from "@/pages/save/profile/PersonalSharingPicker";

export function SharedItemSharingFields({ selection, id, publication }: {
  selection: PersonalShareSelection & { requiredIds?: readonly string[] };
  id?: string;
  publication: { value: boolean; onChange: (value: boolean) => void };
}) {
  const intl = useIntl();
  const t = (key: string) => intl.formatMessage({ id: key });
  const eligible = selection.recipients.filter((group) => group.eligible);
  const retired = selection.recipients.filter((group) => !group.eligible && selection.ids.includes(group.id));
  return <div id={id} className="space-y-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3">
          <span className="text-smycles-body">{t("profile.sharing.publiclyReadable")}</span>
          <Switch checked={publication.value} onChange={publication.onChange}
            ariaLabel={t("profile.sharing.publiclyReadable")} disabled={selection.loading || !!selection.error} />
          </div>
          {publication.value && <p className="text-smycles-meta text-ink-muted">{t("share.create.publiclyReadableHint")}</p>}
        </div>
        {!publication.value && <>
        <div id={id ? `${id}-groups` : undefined} className="flex flex-wrap gap-2" role="group"
          aria-label={t("profile.sharing.chooseGroups")}>
          {eligible.map((group) => {
            const selected = selection.ids.includes(group.id);
            const locked = selected && selection.requiredIds?.includes(group.id) === true;
            return <Button key={group.id} id={id ? `${id}-groups-option-${group.id}` : undefined} variant={selected ? "primary" : "secondary"} size="compact" className="gap-2"
              aria-disabled={locked || undefined} title={locked ? t("share.create.requiredGroup") : undefined}
              aria-pressed={selected} disabled={selection.loading || !!selection.error}
              onClick={() => !locked && selection.onChange(selected
                ? selection.ids.filter((value) => value !== group.id)
                : [...selection.ids, group.id])}>
              {locked && <LockClosedIcon />}
              {groupLabel(intl, group)}
            </Button>;
          })}
        </div>
        {eligible.length === 0 && <p className="text-smycles-body text-ink-muted">{t("profile.sharing.noRecipients")}</p>}
        {retired.length > 0 && <div className="space-y-2">
          <p className="text-smycles-body text-ink-muted">{t("profile.sharing.retired")}</p>
          {retired.map((group) => <div key={group.id} className="flex items-center justify-between gap-2">
            <span className="text-smycles-body">{groupLabel(intl, group)}</span>
            <Button variant="ghost" size="sm" disabled={selection.requiredIds?.includes(group.id)} onClick={() => selection.onChange(selection.ids.filter((id) => id !== group.id))}>
              {t("common.remove")}
            </Button>
          </div>)}
        </div>}

        </>}
  </div>;
}
